import { RawDraftContentState, RawDraftInlineStyleRange } from 'draft-js';

import { IAsset, IWay } from 'components/Pages/Admin/shared/ItinerariesItemLanding/Builder/types';
import { IDetailsScheduleItems } from 'utils/itineraryDetails/types';

import { IListBlockItemData } from './LandingV2/Page/RightSide/blocks/ListBlock/Item/types';
import { ISubtitleData } from './LandingV2/Page/RightSide/blocks/Manifest/types';

export enum EBlockType {
  Text = 'text',
  Checkpoints = 'checkpoints',
  TripEvent = 'trip_event',
  MoreDetails = 'more_details',
  Header = 'header',
  Image = 'image',
  Document = 'document',
  FastFacts = 'list',
  Embed = 'embed',
  Accordion = 'accordion',
  OverviewMap = 'safari_map',
  Costs = 'safari_costs',
  Room = 'room',
  CoverPage = 'cover_page',
  Subtitle = 'subtitle',
}

export interface IRichText {
  text?: string | null;
  initialEditor?: 'text' | 'ai-prompt';
  raw?: RawDraftContentState | null;
}

export interface IPoint {
  text: string;
  inlineStyleRanges: RawDraftInlineStyleRange[];
  entityMap?: any;
}

export interface ITextBlockData extends IRichText {
  template?: 'default' | 'box' | 'notification';
}

export interface ICheckpointsBlockData {
  points: IPoint[];
  template: 'point' | 'line';
}

export interface ITripEventBlockData {
  icon: TTripEventIcon;
  title: string;
  badge?: string;
  badgeColor?: 'violet' | 'red';
}

export type TTripEventIcon = 'airplane-tilt' | 'bed' | 'car' | 'map-pin-line' | 'push-pin' | 'info' | 'fork-knife';

export interface IMoreDetailsBlockData {
  isCollapsible: boolean;
}

interface IHeaderBlockData {
  subheadline: string;
  template?: 'landingV2';
}

export interface IImageBlockData {
  files: IImageDataFile[];
}

export interface IDocumentBlockData {
  files: IAsset[];
}

export interface ISourceBlockData {
  source?: string;
}

export interface IListBlockData {
  items: IListBlockItemData[];
}

export interface IAccordionBlockData {
  spoilers: IAccordionSpoiler[];
}

export interface IAccordionSpoiler {
  /**
   * @deprecated use `children` instead
   */
  body?: IRichText & {
    placeholder?: string;
  };

  children?: TBlock[];
  title: string;
}

export interface IOverviewMapBlockData {
  title: string;
  way: IWay[];
  scheduleType: string;
}

export interface IOverviewBlockData {
  schedules: IDetailsScheduleItems[];
  scheduleType: string;
}

export interface ICoverPageBlockData {
  title: string;
  subtitle?: string | null;
}

interface IImageDataFile {
  id: number;
  url: string;
  name: string;
  tags: string[];
  type: string;
  width: number;
  height: number;
  primary: boolean;
  filename: string;
  mimeType: string;
  accountId: string;
  mediumUrl: string;
  previewUrl: string;
  version400: string;
  version800: string;
  imageCoords: number[];
  storageType: string;
  version1200: string;
  version1600: string;
  version2400: string;
}

export interface IFastFactsBlockData {
  items: IFastFactsItem[];
  template?: 'default' | 'box';
}

interface IFastFactsItem {
  desc: IRichText;
  descPlaceholder: IRichText | null;
  image: IImageDataFile | null;
  imagePlaceholder: string | null;
  title: string;
  titlePlaceholder: string;
}

interface IEmbedBlockData {
  source: string | null;
}

export interface IBlockMetaData {
  readonly?: boolean;
  tripEventId?: number;
  indent?: boolean;
}

export interface IBlock<T extends EBlockType> {
  id: number;
  type: T;
  data: BlockDataMap[T];
  meta?: IBlockMetaData;
  children?: TBlock[];
  _destroy?: boolean;
  availableInBasic?: boolean;
  originalId?: number;
}

type BlockDataMap = {
  [EBlockType.Text]: ITextBlockData;
  [EBlockType.Checkpoints]: ICheckpointsBlockData;
  [EBlockType.TripEvent]: ITripEventBlockData;
  [EBlockType.MoreDetails]: IMoreDetailsBlockData;
  [EBlockType.Header]: IHeaderBlockData;
  [EBlockType.Image]: IImageBlockData;
  [EBlockType.Document]: IDocumentBlockData;
  [EBlockType.FastFacts]: IFastFactsBlockData;
  [EBlockType.Embed]: IEmbedBlockData;
  [EBlockType.Accordion]: IAccordionBlockData;
  [EBlockType.OverviewMap]: IOverviewMapBlockData;
  [EBlockType.Costs]: {};
  [EBlockType.Room]: {};
  [EBlockType.CoverPage]: ICoverPageBlockData;
  [EBlockType.Subtitle]: ISubtitleData;
};

export type TBlock = {
  [K in EBlockType]: IBlock<K>;
}[EBlockType];

interface IPage {
  id: number;
  title: string;
  blocks: TBlock[];
}

const pages: IPage[] = [
  {
    id: 12345,
    title: 'Day 1\nWednesday, December 17 - 2022',
    blocks: [
      {
        id: 123123,
        type: EBlockType.TripEvent,
        data: {
          icon: 'airplane-tilt',
          title: 'Flight from Minsk to Rome',
        },
        meta: {
          readonly: true,
        },
        children: [
          {
            id: 321321,
            type: EBlockType.Checkpoints,
            data: {
              points: [
                { text: '9PM Depart from Minsk', inlineStyleRanges: [] },
                {
                  text: '10PM Arrive into Rome',
                  inlineStyleRanges: [],
                },
              ],
              template: 'point',
            },
            meta: {
              readonly: true,
            },
          },
          {
            id: 123345,
            type: EBlockType.Text,
            data: {
              text: 'Some information text',
              template: 'box',
            },
            meta: {
              readonly: true,
            },
          },
        ],
      },
      {
        id: 423421,
        type: EBlockType.TripEvent,
        data: {
          icon: 'bed',
          title: 'Overnight at Ritz Carlton',
          badge: 'Alternate Option',
        },
        children: [
          {
            id: 512342,
            type: EBlockType.Checkpoints,
            data: {
              points: [
                { text: '1PM - Check In', inlineStyleRanges: [] },
                { text: 'Luxury Suite X1 (Breakfast Included)', inlineStyleRanges: [] },
                { text: '2 Nights', inlineStyleRanges: [] },
              ],
              template: 'line',
            },
            meta: {
              readonly: true,
            },
          },
          {
            id: 2345145,
            type: EBlockType.Text,
            data: {
              text: 'Confirmation Number: AB009\nContact information: +995 598 001122\nWebsite: hotel-eden.com\nLocation: Via Ludovisi, 49, 00187 Roma RM, Italy',
              template: 'box',
              //raw: {} as RawDraftContentState, // here must be draft structure which includes LINK entities
            },
            meta: {
              readonly: true,
            },
          },
          {
            id: 542342,
            type: EBlockType.MoreDetails,
            data: {
              isCollapsible: true,
            },
            meta: {},
            children: [
              {
                id: 5232134,
                type: EBlockType.Text,
                data: {
                  text:
                    'Hotel Eden Rome, Dorchester Collection\n' +
                    'Located in the heart of Rome, Dorchester Collection’s Hotel Eden is just a short stroll from the legendary Spanish Steps and picturesque Villa Borghese. This elegant city oasis dates back to 1889 and throughout its illustrious history has welcomed royalty, dignitaries, and numerous celebrities.',
                  raw: {
                    blocks: [
                      {
                        key: '3tovi',
                        text: 'Hotel Eden Rome, Dorchester Collection',
                        type: 'unstyled',
                        depth: 0,
                        inlineStyleRanges: [{ length: 38, offset: 0, style: 'BOLD' }],
                        entityRanges: [],
                        data: {},
                      },
                      {
                        key: '3vvhk',
                        text: 'Located in the heart of Rome, Dorchester Collection’s Hotel Eden is just a short stroll from the legendary Spanish Steps and picturesque Villa Borghese. This elegant city oasis dates back to 1889 and throughout its illustrious history has welcomed royalty, dignitaries, and numerous celebrities.',
                        type: 'unstyled',
                        depth: 0,
                        inlineStyleRanges: [],
                        entityRanges: [],
                        data: {},
                      },
                    ],
                    entityMap: {},
                  },
                },
                meta: {},
              },
            ],
          },
        ],
        meta: {
          readonly: true,
        },
      },
    ],
  },
];

export default pages;
